// registry.ts — the effect registry with per-effect kill switches (the Vlambeer board's data).
//
// Every effect registers itself by name and hangs off the bus. Whether it actually fires is a
// single gate: the master juice switch AND that effect's own `enabled` flag in feel.json. Flip any
// one off (in Tweakpane, or feel.json) and that effect goes silent while the game logic — which
// knows nothing about any of this — is untouched. If an effect can't be killed alone, it's built
// wrong (JUICE-RIG.md §3).

import { feel } from "./feel";
import { bus, type EventName, type Events } from "../core/bus";

export interface RegisteredEffect {
  /** Must match a key in feel.json (its kill switch + tunables live there). */
  name: string;
  label: string;
}

const effects: RegisteredEffect[] = [];

/**
 * Register an effect and wire its bus subscription. The subscription is pre-gated: the handler
 * only runs when both the master switch and this effect's `enabled` flag are on.
 */
export function registerEffect<K extends EventName>(
  def: RegisteredEffect,
  event: K,
  handler: (payload: Events[K]) => void,
): void {
  effects.push(def);
  bus.on(event, (payload) => {
    if (effectEnabled(def.name)) handler(payload);
  });
}

/** The gate: master juice on AND this effect enabled. */
export function effectEnabled(name: string): boolean {
  if (!feel.master.juice) return false;
  const cfg = (feel as Record<string, any>)[name];
  return !!cfg?.enabled;
}

export function listEffects(): readonly RegisteredEffect[] {
  return effects;
}
